import React, { useCallback, useMemo, useState } from 'react';
import {
  CellProps,
  Column,
  Row as ReactTableRow,
  UseExpandedRowProps,
  useExpanded,
  useTable,
  Cell,
} from 'react-table';

import { ChevronSmallDown as ChevronSmallDownIcon } from '@styled-icons/entypo/ChevronSmallDown';
import { ChevronSmallRight as ChevronSmallRightIcon } from '@styled-icons/entypo/ChevronSmallRight';
import AlphaIBoxOutlineIcon from 'mdi-react/AlphaIBoxOutlineIcon';
import MdiGroupIcon from 'mdi-react/GroupIcon';
import InfoCircleOutlineIcon from 'mdi-react/InfoCircleOutlineIcon';
import { isAxiosError } from 'services/axios';

import {
  Box,
  HStack,
  Icon,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';

import Loader from 'components/Loader';

import { mapConversionInconsistency } from './conversionUtils';
import { fetchBudgetComponentsByParentStageId } from './service';
import { BudgetConversionInconsistency } from './types';

type InconsistenciesTableProps = {
  budgetId: number;
  data: BudgetConversionInconsistency[];
  loading: boolean;
  isMobile: boolean;
  noDataMessage?: string;
  depth?: number;
};

type StageChildrenMap = Record<number, BudgetConversionInconsistency[]>;
type StageLoadMap = Record<number, boolean>;
type StageErrorMap = Record<number, string | null>;

type InconsistencyRow = ReactTableRow<BudgetConversionInconsistency> &
  UseExpandedRowProps<BudgetConversionInconsistency>;

const DEFAULT_NO_DATA_MESSAGE = 'Nenhuma inconsistência encontrada.';
const DEFAULT_STAGE_LOAD_ERROR_MESSAGE =
  'Não foi possível carregar os itens desta etapa.';
const STRUCTURAL_POSITION_INCONSISTENCY = 'Estrutura inválida por posição';

const COLUMN_WIDTHS: Record<string, string> = {
  item: '8%',
  base: '10%',
  code: '10%',
  description: '35%',
  quantity: '8%',
  unitPrice: '9%',
  unitPriceWithBDI: '9%',
  total: '9%',
  inconsistency: '14%',
};

const STAGE_MERGED_COLUMN_IDS = [
  'base',
  'code',
  'description',
  'quantity',
  'unitPrice',
  'unitPriceWithBDI',
];

const NUMERIC_COLUMNS = new Set<string>([
  'quantity',
  'unitPrice',
  'unitPriceWithBDI',
  'total',
]);

const CENTERED_COLUMNS = new Set<string>(['item', 'base']);

const formatCurrency = (value: number | null): string => {
  return Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value || 0);
};

const formatQuantity = (value: number | null): string => {
  return Intl.NumberFormat('pt-BR', {
    maximumFractionDigits: 4,
  }).format(value || 0);
};

const isStageRow = (item: BudgetConversionInconsistency): boolean => {
  return item.type === 'stage';
};

const isCenteredColumn = (columnId: string, isMobile: boolean): boolean => {
  if (columnId === 'item') {
    return !isMobile;
  }

  if (columnId === 'inconsistency') {
    return false;
  }

  return CENTERED_COLUMNS.has(columnId);
};

const getItemIcon = (type: string): React.ElementType => {
  if (type === 'input_original') {
    return InfoCircleOutlineIcon;
  }

  if (type === 'composition_custom') {
    return AlphaIBoxOutlineIcon;
  }

  if (type === 'composition_original') {
    return MdiGroupIcon;
  }

  return ChevronSmallRightIcon;
};

const getCellByColumnId = (
  row: InconsistencyRow,
  columnId: string,
): Cell<BudgetConversionInconsistency> | null => {
  return row.cells.find((cell) => cell.column.id === columnId) || null;
};

const getErrorMessage = (error: unknown): string => {
  if (isAxiosError(error)) {
    return (
      (error.response?.data as { message?: string })?.message ||
      DEFAULT_STAGE_LOAD_ERROR_MESSAGE
    );
  }

  return DEFAULT_STAGE_LOAD_ERROR_MESSAGE;
};

const shouldAddStructuralInconsistency = (
  item: BudgetConversionInconsistency,
  depth: number,
): boolean => {
  return (
    depth > 0 &&
    (item.type === 'input_original' || item.type === 'composition_custom')
  );
};

const getInconsistencyDescription = (
  item: BudgetConversionInconsistency,
  depth: number,
): string => {
  if (!shouldAddStructuralInconsistency(item, depth)) {
    return item.typeLabel;
  }

  return `${item.typeLabel} | ${STRUCTURAL_POSITION_INCONSISTENCY}`;
};

const InconsistenciesTable: React.FC<InconsistenciesTableProps> = ({
  budgetId,
  data,
  loading,
  isMobile,
  noDataMessage = DEFAULT_NO_DATA_MESSAGE,
  depth = 0,
}) => {
  const [stageChildrenById, setStageChildrenById] = useState<StageChildrenMap>(
    {},
  );
  const [stageLoadingById, setStageLoadingById] = useState<StageLoadMap>({});
  const [stageLoadErrorById, setStageLoadErrorById] = useState<StageErrorMap>(
    {},
  );

  const columns = useMemo<Column<BudgetConversionInconsistency>[]>(
    () => [
      {
        Header: 'Item',
        id: 'item',
        accessor: 'location',
        Cell: ({ row }: CellProps<BudgetConversionInconsistency>) => {
          const tableRow = row as InconsistencyRow;
          const isExpandable = isStageRow(tableRow.original);
          const shouldRenderTypeIcon = !isStageRow(tableRow.original);

          return (
            <HStack
              spacing={1}
              justifyContent={isMobile ? 'flex-start' : 'center'}
              alignItems="center"
            >
              {isExpandable &&
                (tableRow.isExpanded ? (
                  <Icon as={ChevronSmallDownIcon} />
                ) : (
                  <Icon as={ChevronSmallRightIcon} />
                ))}

              <Box
                display="flex"
                flexDir="column"
                justifyContent="center"
                alignItems={isMobile ? 'flex-start' : 'center'}
                title={tableRow.original.typeLabel}
                fontWeight="bold"
                minH="40px"
              >
                {tableRow.original.location}
                {shouldRenderTypeIcon && (
                  <Icon w={5} h={5} as={getItemIcon(tableRow.original.type)} />
                )}
              </Box>
            </HStack>
          );
        },
      },
      {
        Header: 'Base',
        id: 'base',
        accessor: 'base',
        Cell: ({ row }: CellProps<BudgetConversionInconsistency>) => {
          return (
            <Text textAlign="center" whiteSpace="pre-wrap">
              {row.original.base}
            </Text>
          );
        },
      },
      {
        Header: 'Código',
        id: 'code',
        accessor: 'code',
      },
      {
        Header: 'Descrição',
        id: 'description',
        accessor: 'description',
        Cell: ({ row }: CellProps<BudgetConversionInconsistency>) => {
          return (
            <Text
              whiteSpace="pre-wrap"
              maxWidth={{ base: '100%', md: '520px' }}
            >
              {row.original.description}
            </Text>
          );
        },
      },
      {
        Header: 'Qtde',
        id: 'quantity',
        accessor: 'quantity',
        Cell: ({ row }: CellProps<BudgetConversionInconsistency>) => {
          return formatQuantity(row.original.quantity);
        },
      },
      {
        Header: 'Unit.',
        id: 'unitPrice',
        accessor: 'unitPrice',
        Cell: ({ row }: CellProps<BudgetConversionInconsistency>) => {
          return formatCurrency(row.original.unitPrice);
        },
      },
      {
        Header: 'Com BDI',
        id: 'unitPriceWithBDI',
        accessor: 'unitPriceWithBDI',
        Cell: ({ row }: CellProps<BudgetConversionInconsistency>) => {
          return formatCurrency(row.original.unitPriceWithBDI);
        },
      },
      {
        Header: 'Total',
        id: 'total',
        accessor: 'total',
        Cell: ({ row }: CellProps<BudgetConversionInconsistency>) => {
          return formatCurrency(row.original.total);
        },
      },
      {
        Header: 'Inconsistência',
        id: 'inconsistency',
        accessor: 'typeLabel',
        Cell: ({ row }: CellProps<BudgetConversionInconsistency>) => {
          return getInconsistencyDescription(row.original, depth);
        },
      },
    ],
    [depth, isMobile],
  );

  const getRowId = useCallback((row: BudgetConversionInconsistency) => {
    return String(row.id);
  }, []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    visibleColumns,
  } = useTable<BudgetConversionInconsistency>(
    {
      columns,
      data,
      getRowId,
      // @ts-expect-error type
      autoResetExpanded: false,
    },
    useExpanded,
  );

  const ensureStageChildrenLoaded = useCallback(
    async (stageId: number): Promise<void> => {
      const hasLoadedChildren = Object.prototype.hasOwnProperty.call(
        stageChildrenById,
        stageId,
      );

      if (hasLoadedChildren || stageLoadingById[stageId]) {
        return;
      }

      setStageLoadingById((previous) => ({
        ...previous,
        [stageId]: true,
      }));
      setStageLoadErrorById((previous) => ({
        ...previous,
        [stageId]: null,
      }));

      try {
        const children = await fetchBudgetComponentsByParentStageId(
          budgetId,
          stageId,
        );
        const mappedChildren = children.map(mapConversionInconsistency);

        setStageChildrenById((previous) => ({
          ...previous,
          [stageId]: mappedChildren,
        }));
      } catch (error) {
        setStageLoadErrorById((previous) => ({
          ...previous,
          [stageId]: getErrorMessage(error),
        }));
      } finally {
        setStageLoadingById((previous) => ({
          ...previous,
          [stageId]: false,
        }));
      }
    },
    [budgetId, stageChildrenById, stageLoadingById],
  );

  const handleToggleStageRow = useCallback(
    (row: InconsistencyRow): void => {
      if (!isStageRow(row.original)) {
        return;
      }

      const shouldExpand = !row.isExpanded;

      row.toggleRowExpanded(shouldExpand);

      if (shouldExpand) {
        ensureStageChildrenLoaded(row.original.id);
      }
    },
    [ensureStageChildrenLoaded],
  );

  const renderExpandedContent = useCallback(
    (row: InconsistencyRow): React.ReactNode => {
      const stageId = row.original.id;

      if (stageLoadingById[stageId]) {
        return <Loader />;
      }

      if (stageLoadErrorById[stageId]) {
        return (
          <Text color="red.600" fontSize="sm">
            {stageLoadErrorById[stageId]}
          </Text>
        );
      }

      const children = stageChildrenById[stageId] || [];

      return (
        <Box>
          <InconsistenciesTable
            budgetId={budgetId}
            data={children}
            loading={false}
            isMobile={isMobile}
            noDataMessage="Nenhum item encontrado nesta etapa."
            depth={depth + 1}
          />
        </Box>
      );
    },
    [
      budgetId,
      depth,
      isMobile,
      stageChildrenById,
      stageLoadErrorById,
      stageLoadingById,
    ],
  );

  return (
    <TableContainer
      width="100%"
      data-testid={depth === 0 ? 'inconsistencies-table' : undefined}
    >
      <Table {...getTableProps()} borderBottom="none" variant="simple">
        <Thead height={12}>
          {headerGroups.map((headerGroup) => {
            const { key: headerGroupKey, ...headerGroupProps } =
              headerGroup.getHeaderGroupProps();

            return (
              <Tr key={String(headerGroupKey)} {...headerGroupProps}>
                {headerGroup.headers.map((column) => {
                  const { key: headerKey, ...headerProps } =
                    column.getHeaderProps();

                  return (
                    <Th
                      key={String(headerKey)}
                      {...headerProps}
                      width={COLUMN_WIDTHS[column.id]}
                      isNumeric={NUMERIC_COLUMNS.has(column.id)}
                      textAlign={
                        isCenteredColumn(column.id, isMobile)
                          ? 'center'
                          : undefined
                      }
                    >
                      {column.render('Header')}
                    </Th>
                  );
                })}
              </Tr>
            );
          })}
        </Thead>

        <Tbody {...getTableBodyProps()}>
          {!loading &&
            rows.length > 0 &&
            rows.map((rawRow) => {
              const row = rawRow as InconsistencyRow;
              prepareRow(row);
              const isExpandable = isStageRow(row.original);
              const isExpanded = isExpandable && row.isExpanded;
              const itemCell = getCellByColumnId(row, 'item');
              const descriptionCell = getCellByColumnId(row, 'description');
              const totalCell = getCellByColumnId(row, 'total');
              const inconsistencyCell = getCellByColumnId(row, 'inconsistency');

              return (
                <React.Fragment key={row.id}>
                  {(() => {
                    const { key: rowKey, ...rowProps } = row.getRowProps();

                    return (
                      <Tr
                        key={String(rowKey)}
                        {...rowProps}
                        data-testid={
                          isExpandable
                            ? `stage-row-${row.original.id}`
                            : undefined
                        }
                        backgroundColor={isExpanded ? 'brand.500' : 'inherit'}
                        color={isExpanded ? 'white' : 'gray.800'}
                        _hover={
                          isExpandable
                            ? {
                                color: isExpanded ? 'white' : 'gray.800',
                                backgroundColor: isExpanded
                                  ? 'brand.500'
                                  : 'gray.200',
                              }
                            : undefined
                        }
                      >
                        {isExpandable &&
                          itemCell &&
                          descriptionCell &&
                          totalCell &&
                          inconsistencyCell && (
                            <>
                              {(() => {
                                const { key: itemCellKey, ...itemCellProps } =
                                  itemCell.getCellProps();

                                return (
                                  <Td
                                    key={String(itemCellKey)}
                                    {...itemCellProps}
                                    py={4}
                                    borderBottomColor={
                                      isExpanded ? 'brand.400' : undefined
                                    }
                                    cursor="pointer"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleToggleStageRow(row);
                                    }}
                                  >
                                    {itemCell.render('Cell')}
                                  </Td>
                                );
                              })()}

                              <Td
                                colSpan={STAGE_MERGED_COLUMN_IDS.length}
                                py={3}
                                borderBottomColor={
                                  isExpanded ? 'brand.400' : undefined
                                }
                                cursor="pointer"
                                textAlign="left"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleToggleStageRow(row);
                                }}
                              >
                                <Box display="flex" flexDir="column">
                                  <Text whiteSpace="pre-wrap" display="block">
                                    {row.original.description}
                                  </Text>
                                </Box>
                              </Td>

                              {(() => {
                                const { key: totalCellKey, ...totalCellProps } =
                                  totalCell.getCellProps();

                                return (
                                  <Td
                                    key={String(totalCellKey)}
                                    {...totalCellProps}
                                    isNumeric
                                    py={4}
                                    borderBottomColor={
                                      isExpanded ? 'brand.400' : undefined
                                    }
                                    cursor="pointer"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleToggleStageRow(row);
                                    }}
                                  >
                                    {totalCell.render('Cell')}
                                  </Td>
                                );
                              })()}

                              {(() => {
                                const {
                                  key: inconsistencyCellKey,
                                  ...inconsistencyCellProps
                                } = inconsistencyCell.getCellProps();

                                return (
                                  <Td
                                    key={String(inconsistencyCellKey)}
                                    {...inconsistencyCellProps}
                                    py={4}
                                    borderBottomColor={
                                      isExpanded ? 'brand.400' : undefined
                                    }
                                    cursor="pointer"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleToggleStageRow(row);
                                    }}
                                  >
                                    {inconsistencyCell.render('Cell')}
                                  </Td>
                                );
                              })()}
                            </>
                          )}

                        {!isExpandable &&
                          row.cells.map((cell) => {
                            const { key: cellKey, ...cellProps } =
                              cell.getCellProps();

                            return (
                              <Td
                                key={String(cellKey)}
                                {...cellProps}
                                isNumeric={NUMERIC_COLUMNS.has(cell.column.id)}
                                py={4}
                                borderBottomColor={
                                  isExpanded ? 'brand.400' : undefined
                                }
                                cursor={isExpandable ? 'pointer' : 'default'}
                                onClick={(event) => {
                                  event.stopPropagation();

                                  if (isExpandable) {
                                    handleToggleStageRow(row);
                                  }
                                }}
                              >
                                {cell.render('Cell')}
                              </Td>
                            );
                          })}
                      </Tr>
                    );
                  })()}

                  {isExpanded && (
                    <Tr
                      backgroundColor="brand.100"
                      border="1px solid white"
                      sx={{
                        '* > table': {
                          backgroundColor: 'white',
                          borderBottom: 'none',
                        },
                      }}
                    >
                      <Td
                        colSpan={visibleColumns.length}
                        paddingY={4}
                        borderBottom="none"
                      >
                        {renderExpandedContent(row)}
                      </Td>
                    </Tr>
                  )}
                </React.Fragment>
              );
            })}

          {loading && (
            <Tr>
              <Td
                colSpan={visibleColumns.length}
                borderBottom="none"
                background="gray.100"
              >
                <Loader />
              </Td>
            </Tr>
          )}

          {!loading && data.length === 0 && (
            <Tr>
              <Td
                colSpan={visibleColumns.length}
                borderBottom="none"
                background="gray.100"
                className="text-center"
              >
                {noDataMessage}
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default InconsistenciesTable;
